import { useTranslation } from "react-i18next";
import { CompanyLogo } from "../../components/CompanyLogo";
import { Card, ErrorBox, Skeleton } from "../../components/ui";
import { usePeopleOverview } from "../../lib/api";
import { fmtRelative, localeFor, parseFrappeDatetime } from "../../lib/format";
import { CERT_PILL, PHASE_PILL } from "./shared";

/** L&D overview (WP-23.1, OS-0615): headcount and certification standing, the client's five-phase roadmap, the
 * certifications needing attention and the training calendar. Reads only the People overview the module shell
 * has already fetched, so this screen costs nothing extra to open. */
export default function LearningHub({ company }: { company?: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const ov = usePeopleOverview(company);
  const data = ov.data;
  const fmtDate = (s: string | null | undefined) => (s ? new Intl.DateTimeFormat(localeFor(lng), { dateStyle: "medium" }).format(new Date(`${s.slice(0, 10)}T00:00:00`)) : "–");
  const fmtDateTime = (s: string) => { const d = parseFrappeDatetime(s); return d ? new Intl.DateTimeFormat(localeFor(lng), { dateStyle: "medium", timeStyle: "short" }).format(d) : s; };
  const counts = data?.certifications.counts ?? {};

  if (ov.isError) return <div className="mt-6"><ErrorBox error={ov.error} onRetry={() => void ov.refetch()} /></div>;
  if (ov.isLoading || !data) return <div className="mt-6 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;

  return (
    <div data-ld-hub>
      {/* headcount */}
      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card className="px-4 py-3">
          <div className="font-heading text-2xl font-semibold">{data.employees_total}</div>
          <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t("ppl.headcount")}</div>
        </Card>
        {(["Expired", "Expiring", "Valid"] as const).map((s) => (
          <Card key={s} className="px-4 py-3">
            <div className={`font-heading text-2xl font-semibold ${s === "Expired" ? "text-status-red" : s === "Expiring" ? "text-status-amber" : "text-status-green"}`}>{counts[s] ?? 0}</div>
            <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t(`ppl.cert.${s}`)} · {t("ppl.certifications")}</div>
          </Card>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
        {data.headcount.map((h) => (
          <Card key={h.company} className="px-4 py-3">
            <div className="flex items-center justify-between">
              <CompanyLogo company={h.company} size={20} showName className="text-sm font-semibold" />
              <span className="font-heading text-xl font-semibold">{h.total}</span>
            </div>
            <div className="mt-1 text-[11px] text-brand-grey">{t("ppl.women")} {h.total ? Math.round((100 * h.women) / h.total) : 0} % · {h.departments.slice(0, 3).map((d) => `${d.department} ${d.n}`).join(" · ")}</div>
          </Card>
        ))}
      </div>
      {/* L&D roadmap */}
      <section className="mt-6">
        <h2 className="font-heading text-base font-semibold">{t("ppl.roadmap")} <span className="text-xs font-normal text-brand-grey">· {t("ppl.roadmapHint")}</span></h2>
        <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-5">
          {data.roadmap.map((ph) => (
            <Card key={ph.name} className="flex flex-col p-3">
              <div className="flex items-center justify-between">
                <span className="font-heading text-xs font-semibold text-brand-grey">{t("ppl.phase")} {ph.phase_no}</span>
                <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${PHASE_PILL[ph.derived_status] ?? PHASE_PILL.Planned}`}>{t(`ppl.phaseStatus.${ph.derived_status}`, ph.derived_status)}</span>
              </div>
              <div className="mt-1 font-heading text-sm font-semibold leading-snug">{ph.title}</div>
              <div className="text-[11px] text-brand-grey">{fmtDate(ph.planned_start)} → {fmtDate(ph.planned_end)}</div>
              <div className="mt-2 h-1.5 rounded bg-brand-grey-light"><div className="h-1.5 rounded bg-brand-primary" style={{ width: `${ph.progress}%` }} /></div>
              <ul className="mt-2 space-y-0.5 text-[11px]">
                {ph.programs.map((p) => (
                  <li key={p.name} className="flex items-start gap-1">
                    <span className={p.status === "Completed" ? "text-status-green" : "text-brand-grey"}>{p.status === "Completed" ? "✓" : "○"}</span>
                    <span className={p.status ? "" : "italic text-brand-grey"}>{p.name}</span>
                  </li>
                ))}
              </ul>
              {ph.deliverables && <div className="mt-2 text-[10px] leading-snug text-brand-grey">{ph.deliverables}</div>}
            </Card>
          ))}
        </div>
      </section>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* certifications needing attention */}
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-brand-grey-light px-4 py-3">
            <h2 className="font-heading text-base font-semibold">{t("ppl.certAttention")}</h2>
            <span className="text-xs text-brand-grey">{t("ppl.certHint", { n: data.certifications.warn_days })}</span>
          </div>
          {data.certifications.attention.length === 0 ? (
            <div className="p-6 text-center text-sm text-brand-grey">{t("ppl.noCertAttention")}</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.certifications.attention.map((c) => (
                  <tr key={c.name} className="border-t border-brand-grey-light">
                    <td className="px-4 py-2"><span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${CERT_PILL[c.status]}`}>{t(`ppl.cert.${c.status}`)}</span></td>
                    <td className="px-4 py-2"><div className="font-semibold">{c.employee_name}</div><div className="text-[11px] text-brand-grey"><CompanyLogo company={c.company} size={12} className="mr-1 align-middle" />{c.designation}</div></td>
                    <td className="px-4 py-2 text-xs">{c.certification_type}</td>
                    <td className="px-4 py-2 text-right text-xs"><div>{fmtDate(c.expires_on)}</div><div className={c.days_to_expiry != null && c.days_to_expiry < 0 ? "text-status-red" : "text-brand-grey"}>{c.days_to_expiry != null ? (c.days_to_expiry < 0 ? t("proj.overdue", { n: -c.days_to_expiry }) : t("proj.daysLeft", { n: c.days_to_expiry })) : ""}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* training calendar */}
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-brand-grey-light px-4 py-3">
            <h2 className="font-heading text-base font-semibold">{t("ppl.training")}</h2>
            <span className="text-xs text-brand-grey">{data.programs.length} {t("ppl.programs")}</span>
          </div>
          {data.events.length === 0 ? (
            <div className="p-6 text-center text-sm text-brand-grey">{t("ppl.noEvents")}</div>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {data.events.map((e) => (
                  <tr key={e.name} className="border-t border-brand-grey-light">
                    <td className="px-4 py-2"><span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${e.event_status === "Completed" ? PHASE_PILL.Completed : e.event_status === "Cancelled" ? CERT_PILL.Expired : PHASE_PILL["In Progress"]}`}>{t(`ppl.eventStatus.${e.event_status}`, e.event_status)}</span></td>
                    <td className="px-4 py-2"><div className="font-semibold">{e.event_name}</div><div className="text-[11px] text-brand-grey">{e.training_program} · {e.location}</div></td>
                    <td className="px-4 py-2 text-right text-xs"><div>{fmtDateTime(e.start_time)}</div><div className="text-brand-grey">{e.present}/{e.invited} {t("ppl.attendees")}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
      <p className="mt-2 text-[11px] text-brand-grey">{t("ppl.updated")} {fmtRelative(parseFrappeDatetime(data.as_of), lng)}</p>
    </div>
  );
}
